import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import MemoryTimeline from "../components/MemoryTimeline";
import { Gift, Download, Sprout, Loader2, Clock, Heart, Sparkles } from "lucide-react";
import { toast } from "sonner";

const Doni = () => {
  const [gifts, setGifts] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/my-gifts").then((res) => setGifts(res.data)).catch(() => setGifts([]));
  useEffect(() => { load(); }, []);

  const downloadArchive = async (token, ownerName) => {
    setBusy(`archive-${token}`);
    try {
      const res = await api.get(`/heir/archive/${token}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `archivio-${ownerName}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Archivio scaricato");
    } catch {
      toast.error("Impossibile scaricare l'archivio in questo momento");
    } finally { setBusy(null); }
  };

  const importGift = async (token) => {
    setBusy(`import-${token}`);
    try {
      const res = await api.post(`/heir/import/${token}`);
      const d = res.data;
      let msg = `${d.imported} ricordi importati nella tua Eterna`;
      if (d.ancestors_imported) msg += ` · ${d.ancestors_imported} antenati`;
      if (d.skipped_existing) msg += ` · ${d.skipped_existing} già presenti`;
      if (d.skipped_space) msg += ` · ${d.skipped_space} saltati per spazio`;
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nell'importazione");
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-10 fade-in">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#FBEDE7] flex items-center justify-center">
            <Gift className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="font-serif text-4xl tracking-tight">La tua cassetta dei doni</h1>
            <p className="text-muted-foreground mt-1">I lasciti che le persone care hanno custodito per te, raccolti in un unico luogo.</p>
          </div>
        </div>

        {gifts === null ? (
          <p className="text-muted-foreground">Caricamento…</p>
        ) : gifts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-border" data-testid="gifts-empty">
            <Heart className="w-8 h-8 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground max-w-md mx-auto">Al momento non risultano doni a te dedicati con questa email. Appariranno qui quando qualcuno ti sceglierà come erede e sarà il momento giusto.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {gifts.map((g) => (
              <section key={g.heir_token} className="bg-white rounded-3xl border border-border p-7 sm:p-8" data-testid={`gift-${g.heir_token}`}>
                <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                  <div>
                    <p className="text-sm text-primary">Un dono da</p>
                    <h2 className="font-serif text-2xl">{g.owner_name}{g.relationship ? ` · ${g.relationship}` : ""}</h2>
                  </div>
                  {g.released ? (
                    <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full text-[#4e8f6f] bg-[#EAF3EE]">Disponibile</span>
                  ) : g.memories.length > 0 ? (
                    <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full text-[#a06a1f] bg-[#F7EFE0]"><Sparkles className="w-3.5 h-3.5" /> Un anticipo per te</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full text-muted-foreground bg-[#F4F1EA]"><Clock className="w-3.5 h-3.5" /> In attesa</span>
                  )}
                </div>

                {g.welcome_message && (
                  <p className="text-[#3d3a38] leading-relaxed whitespace-pre-wrap mb-6 border-l-2 border-primary/30 pl-4 italic">{g.welcome_message}</p>
                )}

                {g.memories.length > 0 ? (
                  <>
                    {g.released && (
                      <div className="flex flex-wrap gap-3 mb-6">
                        <button onClick={() => downloadArchive(g.heir_token, g.owner_name)} disabled={!!busy} className="inline-flex items-center gap-2 rounded-full bg-white border border-border h-11 px-6 text-sm hover:border-primary/40 transition-colors disabled:opacity-60" data-testid={`gift-download-${g.heir_token}`}>
                          {busy === `archive-${g.heir_token}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Scarica l'archivio
                        </button>
                        <button onClick={() => importGift(g.heir_token)} disabled={!!busy} className="inline-flex items-center gap-2 rounded-full bg-primary text-white h-11 px-6 text-sm hover:bg-[#c9694f] transition-colors disabled:opacity-60" data-testid={`gift-import-${g.heir_token}`}>
                          {busy === `import-${g.heir_token}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sprout className="w-4 h-4" />} Importa nella tua Eterna
                        </button>
                      </div>
                    )}
                    <MemoryTimeline memories={g.memories} guardianToken={g.heir_token} ownerName={g.owner_name} coverImage={g.cover_image} />
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">I ricordi saranno disponibili al momento previsto da {g.owner_name}.</p>
                )}
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Doni;
